import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { RegistrationUploadMedia } from "../redux/features/CommonService";
import {
  createFilePreviewUrl,
  revokePreviewUrl,
} from "../utils/grantorLicenseUpload";

const useGrantorLicenseUpload = (formik) => {
  const dispatch = useDispatch();
  const fileFrontRef = useRef(null);
  const fileBackRef = useRef(null);
  const [frontPreview, setFrontPreview] = useState(null);
  const [backPreview, setBackPreview] = useState(null);
  const [isUploadingFront, setIsUploadingFront] = useState(false);
  const [isUploadingBack, setIsUploadingBack] = useState(false);

  useEffect(() => {
    return () => {
      revokePreviewUrl(frontPreview);
      revokePreviewUrl(backPreview);
    };
  }, [frontPreview, backPreview]);

  const handleUploadImage = async (file) => {
    if (!file) {
      return null;
    }

    let data = file;
    if (file?.type === "image/svg+xml") {
      data = new Blob([file], { type: "image/svg" });
      data.name = file.name;
    }

    const formData = new FormData();
    formData.append("files", data, file.name);
    const res = await dispatch(RegistrationUploadMedia(formData));

    if (res.type === "commons/RegistrationUploadMedia/fulfilled") {
      return res.payload?.files[0];
    }

    toast.error("Failed to upload file. Please try again.", { autoClose: 3000 });
    return null;
  };

  const handleLicenseUpload = async ({
    file,
    currentPreview,
    setPreview,
    setIsUploading,
    mediaField,
    showField,
    touchField,
    errorMessage,
  }) => {
    if (!file) {
      return;
    }

    revokePreviewUrl(currentPreview);
    const previewUrl = createFilePreviewUrl(file);
    setPreview(previewUrl);
    formik.setFieldValue(showField, file.name);
    formik.setFieldTouched(touchField, true, false);
    setIsUploading(true);

    const uploadData = await handleUploadImage(file);
    setIsUploading(false);

    if (uploadData?.mediaName) {
      formik.setFieldValue(mediaField, uploadData.mediaName);
    } else {
      formik.setFieldValue(mediaField, "");
      formik.setFieldValue(showField, "");
      revokePreviewUrl(previewUrl);
      setPreview(null);
      toast.error(errorMessage, { autoClose: 3000 });
    }
  };

  const drivingLicenseFront = async (e) => {
    await handleLicenseUpload({
      file: e.target.files[0],
      currentPreview: frontPreview,
      setPreview: setFrontPreview,
      setIsUploading: setIsUploadingFront,
      mediaField: "drivingLicenseFront",
      showField: "drivingLicenseFrontShow",
      touchField: "drivingLicenseFront",
      errorMessage: "Failed to upload Driver's License Front. Please try again.",
    });
    e.target.value = "";
  };

  const drivingLicenseBack = async (e) => {
    await handleLicenseUpload({
      file: e.target.files[0],
      currentPreview: backPreview,
      setPreview: setBackPreview,
      setIsUploading: setIsUploadingBack,
      mediaField: "drivingLicenseBack",
      showField: "drivingLicenseBackShow",
      touchField: "drivingLicenseBack",
      errorMessage: "Failed to upload Driver's License Back. Please try again.",
    });
    e.target.value = "";
  };

  const removeDrivingLicenseFront = () => {
    revokePreviewUrl(frontPreview);
    setFrontPreview(null);
    formik.setFieldValue("drivingLicenseFront", "");
    formik.setFieldValue("drivingLicenseFrontShow", "");
    formik.setFieldTouched("drivingLicenseFront", true, false);
    if (fileFrontRef.current) {
      fileFrontRef.current.value = "";
    }
  };

  const removeDrivingLicenseBack = () => {
    revokePreviewUrl(backPreview);
    setBackPreview(null);
    formik.setFieldValue("drivingLicenseBack", "");
    formik.setFieldValue("drivingLicenseBackShow", "");
    formik.setFieldTouched("drivingLicenseBack", true, false);
    if (fileBackRef.current) {
      fileBackRef.current.value = "";
    }
  };

  return {
    fileFrontRef,
    fileBackRef,
    frontPreview,
    backPreview,
    isUploadingFront,
    isUploadingBack,
    isUploading: isUploadingFront || isUploadingBack,
    handleSvgFrontClick: () => fileFrontRef.current?.click(),
    handleSvgBackClick: () => fileBackRef.current?.click(),
    drivingLicenseFront,
    drivingLicenseBack,
    removeDrivingLicenseFront,
    removeDrivingLicenseBack,
  };
};

export default useGrantorLicenseUpload;
